import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAnalytics } from 'hooks/use-analytics'
import { ApiError, IntegrationsService } from 'openapi'
import { apiCall } from 'modules/core/services/api'
import {
  FallbackApi,
  type FallbackPageSummary,
  type FallbackPlatform,
  type FallbackProviderId,
} from '../api/fallback-api'
import { GatewayApi, type GatewayMode } from '../api/gateway-api'
import { launchPhylloConnect } from './use-fallback-connect'
import { classifyClientConnectError } from '../utils/connect-failure-class'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'

// Distinct from the picker's key so a Composio redirect started from the main
// Integrations Connect button resumes here, not in the empty-Pages picker.
const PENDING_KEY = 'taka_gateway_pending'

export interface PendingConnect {
  gateway: FallbackProviderId
  externalUserId: string
  platform: FallbackPlatform
  // Correlates this attempt across connect-session → listPages → select-page in
  // server logs, error tracking and analytics. Optional: older pending markers lack it.
  connectAttemptId?: string
  // Present only for the single-account Instagram/Composio finalize: routes the
  // resume to select-account (GET account → POST select-account) instead of the
  // Page picker. Absent for the Facebook/fallback picker flows.
  provider?: FallbackPlatform
}

interface GatewayPickerState extends PendingConnect {
  pages: FallbackPageSummary[]
}

/**
 * Rendered by the consumer as a ConfirmDialog when Instagram's select-account
 * returns 409 (the newly authorized IG account differs from the previously
 * connected one). The consumer calls `confirmMismatch()` to retry with force,
 * or `cancelMismatch()` to bail. The hook can't render the dialog itself
 * (hooks don't render), so it just exposes the pending state.
 */
interface InstagramMismatch {
  previousHandle: string
  newHandle: string
}

/**
 * Drives the routed Connect button: asks the server which gateway to use for a
 * platform, then either tells the caller to run our own OAuth popup (`direct`)
 * or drives the partner flow itself (Composio redirect / Phyllo SDK) and surfaces
 * the linked Pages for selection via an inline picker. On a redirect round-trip
 * it resumes on mount. `onConnected` fires after a Page is persisted so the
 * caller can refetch.
 */
export function useGatewayConnect(opts: {
  onConnected: () => void
  // Fired when a step after the partner link fails (already tracked and reported).
  onFailed?: () => void
}) {
  const { onConnected, onFailed } = opts
  const { track } = useAnalytics()
  const [picker, setPicker] = useState<GatewayPickerState | null>(null)
  const [selecting, setSelecting] = useState(false)
  // Set when Instagram's select-account returns 409 (account-match guard).
  // The consumer renders a ConfirmDialog based on this state; on confirm it
  // calls `confirmMismatch()`, which retries with force:true.
  const [pendingMismatch, setPendingMismatch] = useState<InstagramMismatch | null>(null)
  const pendingMismatchCtxRef = useRef<PendingConnect | null>(null)
  const onConnectedRef = useRef(onConnected)
  onConnectedRef.current = onConnected
  const onFailedRef = useRef(onFailed)
  onFailedRef.current = onFailed

  // Dual-emit a partner-gateway connect failure: the deprecated FALLBACK_CONNECT_FAILED
  // plus the unified SOCIAL_CONNECT_FAILED, during the migration window. Mirrors the
  // `trackFailed` helper in use-fallback-connect.ts.
  const trackConnectFailed = useCallback(
    (
      step: string,
      err: unknown,
      ctx: { platform: string; gateway: FallbackProviderId; connectAttemptId?: string }
    ) => {
      const error = err instanceof Error ? err.message : String(err)
      const failureClass = classifyClientConnectError(err)
      // Only real errors reach error tracking/console; a user cancel must not page us.
      if (failureClass !== 'user_aborted') {
        console.error(`[gateway-connect] ${step} failed:`, err)
        errorReportingService.captureException(err instanceof Error ? err : new Error(error), {
          feature: 'social_connect',
          step,
          social_gateway: ctx.gateway,
          platform: ctx.platform,
          failure_class: failureClass,
        })
      }
      track(AnalyticsEvents.Social.FALLBACK_CONNECT_FAILED, {
        platform: ctx.platform,
        provider: ctx.gateway,
        error,
        step,
        connect_attempt_id: ctx.connectAttemptId,
      })
      track(AnalyticsEvents.Social.SOCIAL_CONNECT_FAILED, {
        platform: ctx.platform,
        social_gateway: ctx.gateway,
        error,
        step,
        failure_class: failureClass,
        connect_attempt_id: ctx.connectAttemptId,
      })
      onFailedRef.current?.()
    },
    [track]
  )

  /**
   * Finalize a single-account Instagram → Composio connect after the hosted
   * consent redirects back. IG has no Page picker (the connection is bound to
   * exactly one IG Business account at consent), so instead of listing Pages we
   * fetch the single account and persist it via select-account. On a 409 (the reconnect authorized a DIFFERENT IG account
   * than the one previously stored) we confirm with the user and retry with
   * `force: true`, matching the server's account-match guard contract.
   */
  // One shared select-account call. `force` is the only difference between
  // the initial attempt and the post-confirm retry, so lift it into a param
  // rather than copy-pasting the call.
  const selectInstagramAccount = useCallback(
    (ctx: PendingConnect, force: boolean) =>
      apiCall(IntegrationsService.integrationControllerSelectInstagramAccountV1, null, {
        ...(force ? { force: true } : {}),
        connectAttemptId: ctx.connectAttemptId,
      }),
    []
  )

  const finalizeInstagramAccount = useCallback(
    async (ctx: PendingConnect) => {
      try {
        // GET the single account first so a confirm can show the incoming handle.
        const account = await apiCall(
          IntegrationsService.integrationControllerGetInstagramAccountV1,
          null
        )
        try {
          await selectInstagramAccount(ctx, false)
        } catch (err) {
          // 409 = account-match guard: the newly authorized IG account differs
          // from the previously connected one. Hand the consumer a mismatch
          // state so it can render a ConfirmDialog; the retry with force:true
          // runs in `confirmMismatch` below.
          if (err instanceof ApiError && err.status === 409) {
            const previousHandle =
              (err.body?.previousHandle as string | undefined) ?? 'the previous account'
            const newHandle =
              (err.body?.newHandle as string | undefined) ?? account.handle ?? 'this account'
            pendingMismatchCtxRef.current = ctx
            setPendingMismatch({ previousHandle, newHandle })
            return
          }
          throw err
        }
        // SOCIAL_CONNECT_SUCCEEDED is emitted server-side in select-account.
        onConnectedRef.current()
      } catch (err) {
        console.error('[gateway-connect] instagram_upgrade_finalize failed:', err)
        trackConnectFailed('instagram_upgrade_finalize', err, {
          platform: 'instagram',
          gateway: 'composio',
          connectAttemptId: ctx.connectAttemptId,
        })
      }
    },
    [trackConnectFailed, selectInstagramAccount]
  )

  const confirmMismatch = useCallback(async () => {
    const ctx = pendingMismatchCtxRef.current
    if (!ctx) return
    try {
      await selectInstagramAccount(ctx, true)
      onConnectedRef.current()
    } catch (err) {
      console.error('[gateway-connect] instagram_upgrade_finalize failed:', err)
      trackConnectFailed('instagram_upgrade_finalize', err, {
        platform: 'instagram',
        gateway: 'composio',
        connectAttemptId: ctx.connectAttemptId,
      })
    } finally {
      pendingMismatchCtxRef.current = null
      setPendingMismatch(null)
    }
  }, [selectInstagramAccount, trackConnectFailed])

  const cancelMismatch = useCallback(() => {
    pendingMismatchCtxRef.current = null
    setPendingMismatch(null)
  }, [])

  const loadPages = useCallback(
    async (ctx: PendingConnect) => {
      try {
        const res = await FallbackApi.listPages({
          provider: ctx.gateway,
          externalUserId: ctx.externalUserId,
          platform: ctx.platform,
          connectAttemptId: ctx.connectAttemptId,
        })
        setPicker({ ...ctx, pages: res.pages ?? [] })
      } catch (err) {
        console.error('[gateway-connect] list_pages failed:', err)
        trackConnectFailed('list_pages', err, {
          platform: ctx.platform,
          gateway: ctx.gateway,
          connectAttemptId: ctx.connectAttemptId,
        })
      }
    },
    [trackConnectFailed]
  )

  // Finish a redirect-mode (Composio) connection after the browser returns.
  // Instagram (single-account) takes the select-account finalize; the
  // Facebook/fallback picker flows list Pages for selection.
  const resume = useCallback(
    async (ctx: PendingConnect) => {
      if (ctx.provider === 'instagram' && ctx.gateway === 'composio') {
        await finalizeInstagramAccount(ctx)
      } else {
        await loadPages(ctx)
      }
    },
    [loadPages, finalizeInstagramAccount]
  )

  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_KEY)
    if (!raw) return
    sessionStorage.removeItem(PENDING_KEY)
    let ctx: PendingConnect
    try {
      ctx = JSON.parse(raw) as PendingConnect
    } catch {
      return // ignore malformed marker
    }
    void resume(ctx)
  }, [resume])

  /**
   * Resolve the gateway for `platform` and start connecting. Returns the mode:
   * for `oauth_popup` the caller must run our own OAuth popup; for `redirect`/`sdk`
   * the partner flow is already driven here. `failed` means the connect couldn't
   * start; the caller shows an error rather than guessing a gateway.
   */
  const resolveAndStart = useCallback(
    async (platform: FallbackPlatform): Promise<GatewayMode | 'failed'> => {
      let session
      try {
        session = await GatewayApi.connectSession(platform)
      } catch (err) {
        // No direct fallback: guessing direct would move a business pinned to a
        // partner gateway off it. connect_session runs before any provider
        // interaction, so this is always a real error (never a cancel).
        console.error('[gateway-connect] connect_session failed:', err)
        errorReportingService.captureException(
          err instanceof Error ? err : new Error(String(err)),
          {
            feature: 'social_connect',
            step: 'connect_session',
            social_gateway: 'composio',
            platform,
            failure_class: 'provider_error',
          }
        )
        track(AnalyticsEvents.Social.FALLBACK_CONNECT_FAILED, {
          platform,
          provider: 'unknown',
          error: err instanceof Error ? err.message : String(err),
          step: 'connect_session',
        })
        // No social_connect_failed: the gateway is unknown and no
        // social_connect_initiated was emitted for this attempt.
        return 'failed'
      }

      if (session.mode === 'oauth_popup') return 'oauth_popup'

      const connectAttemptId = session.connectAttemptId

      track(AnalyticsEvents.Social.FALLBACK_CONNECT_STARTED, {
        platform,
        provider: session.gateway,
        connect_attempt_id: connectAttemptId,
      })
      // Unified connect funnel (dual-emit during migration). Single client-side
      // emitter for a partner-gateway connect start; the gateway is now resolved.
      track(AnalyticsEvents.Social.SOCIAL_CONNECT_INITIATED, {
        platform,
        social_gateway: session.gateway,
        source: 'integrations_page',
        connect_attempt_id: connectAttemptId,
      })

      if (session.mode === 'redirect') {
        sessionStorage.setItem(
          PENDING_KEY,
          JSON.stringify({
            gateway: session.gateway,
            externalUserId: session.externalUserId,
            platform,
            connectAttemptId,
          } satisfies PendingConnect)
        )
        window.location.href = session.redirectUrl
        return 'redirect'
      }

      // sdk mode (Phyllo): launch the web SDK inline, then list linked pages.
      try {
        await launchPhylloConnect(session)
        await loadPages({
          gateway: session.gateway,
          externalUserId: session.externalUserId,
          platform,
          connectAttemptId,
        })
      } catch (err) {
        console.error('[gateway-connect] sdk_link failed:', err)
        trackConnectFailed('sdk_link', err, {
          platform,
          gateway: session.gateway,
          connectAttemptId,
        })
      }
      return 'sdk'
    },
    [track, loadPages, trackConnectFailed]
  )

  const selectPage = useCallback(
    async (externalAccountId: string) => {
      if (!picker) return
      setSelecting(true)
      try {
        await FallbackApi.selectPage({
          provider: picker.gateway,
          externalAccountId,
          externalUserId: picker.externalUserId,
          platform: picker.platform,
          connectAttemptId: picker.connectAttemptId,
        })
        // FALLBACK_CONNECT_SUCCEEDED is emitted server-side in selectFallbackPage
        // (the single emitter — it also carries the managed/personal page-source
        // breakdown). Emitting here too would double-count in Mixpanel.
        setPicker(null)
        onConnectedRef.current()
      } catch (err) {
        console.error('[gateway-connect] select_page failed:', err)
        trackConnectFailed('select_page', err, {
          platform: picker.platform,
          gateway: picker.gateway,
          connectAttemptId: picker.connectAttemptId,
        })
      } finally {
        setSelecting(false)
      }
    },
    [picker, trackConnectFailed]
  )

  const closePicker = useCallback(() => setPicker(null), [])

  return {
    resolveAndStart,
    resume,
    picker,
    selecting,
    selectPage,
    closePicker,
    pendingMismatch,
    confirmMismatch,
    cancelMismatch,
  }
}
